import { useMemo } from 'react';
import { IBuilding, IBuildingItem } from 'models';
import { useFetchBuildingQuery, useFetchBuildingsQuery } from 'store/apis';
import { selectCurrentUserInfo } from 'store/slices/auth.slice';
import { useAppSelector } from 'store/store.hook';
import { filterBuildingItems, getBuildingImageUrl, getBuildingLocation } from 'utils';
import { useZones } from 'pages/zones/zones.hook';
import { useMatch } from 'react-router-dom';
import { ROUTES } from 'consts';

export const useBuildings = (): [boolean, IBuilding[]] => {
  const { orgSlug } = useAppSelector(selectCurrentUserInfo);
  const { data: buildings, isLoading } = useFetchBuildingsQuery({ orgSlug });

  return useMemo(() => {
    return [isLoading, buildings || []];
  }, [isLoading, buildings]);
};

type UseBuildingItemsProps = {
  searchKeyword: string;
  city?: string;
  state?: string;
  account_name?: string;
};

export const useBuildingItems = ({
  searchKeyword,
  city = '',
  state = '',
  account_name = '',
}: UseBuildingItemsProps): [boolean, IBuildingItem[], IBuildingItem[]] => {
  const [loadingBuildings, buildings] = useBuildings();
  const [loadingZones, { active: activeZones }] = useZones({ buildingId: null });

  const allBuildingItems = useMemo(() => {
    return (buildings || []).map((building) => {
      const { id, created_at, account_name, city, state } = building;
      const thumbnail = getBuildingImageUrl(building);
      const location = getBuildingLocation(building);
      const floors = building.floors.length;
      const zones = activeZones.filter((zone) => zone.building_id === id).length;

      return {
        account_name: account_name ?? '',
        city,
        state,
        id,
        thumbnail,
        location,
        floors,
        zones,
        created_at,
      } as IBuildingItem;
    });
  }, [buildings, activeZones]);

  const filteredBuildingItems = useMemo(() => {
    return filterBuildingItems(allBuildingItems, { searchKeyword, city, state, account_name });
  }, [allBuildingItems, searchKeyword, city, state, account_name]);

  const loading = loadingBuildings || loadingZones;

  return [loading, filteredBuildingItems, allBuildingItems];
};

export type UseBuildingProps = {
  buildingId: number;
};

export const useBuilding = ({ buildingId }: UseBuildingProps): [boolean, IBuilding] => {
  const { orgSlug } = useAppSelector(selectCurrentUserInfo);
  const { data, isLoading } = useFetchBuildingQuery({ orgSlug, id: buildingId });

  return useMemo(() => [isLoading, data], [isLoading, data]);
};

export const useActiveBuildingPage = () => {
  const isActivePage = useMatch(`${ROUTES.ORG_SLUG}/${ROUTES.BUILDING_DETAILS}/${ROUTES.ZONES_ACTIVE}`);
  const isInactivePage = useMatch(`${ROUTES.ORG_SLUG}/${ROUTES.BUILDING_DETAILS}/${ROUTES.ZONES_INACTIVE}`);

  return {
    isActivePage: !!isActivePage,
    isInactivePage: !!isInactivePage,
    tabKey: isActivePage ? 'active' : isInactivePage ? 'inactive' : null,
  } as { isActivePage: boolean; isInactivePage: boolean; tabKey: 'active' | 'inactive' | null };
};
